import { apiRequest } from './index';
import sleep from '../sleep';

const MERGE_RESUME_URL = '/api/profile/merged-resume';

// possible merge_resume_status states
export const FAILURE = 'failure';
export const SUCCESS = 'success';
export const NEEDS_FIX = 'needs_fix';
export const WAIT = 'wait';

// limit polling to 8 requests
// this will be 12.8 seconds for the last attempt with exponential backoff
const ATTEMPT_LIMIT = 8;
// first backoff will be 100 ms
const BASE_BACKOFF = 100;

/**
 * This is not exported. This is a recursive helper for tryMergedResume. See tryMergedResume which uses this.
 * @param {object} options object of options for the different results, see tryMergedResume
 * @param {number} pollOptions.attemptLimit max number of attempts when polling
 * @param {number} pollOptions.baseBackoff base backoff wait period in ms when polling, this is doubled after each attempt
 * @param {number} state.attempt number representing poll retries, if max is reached polling is ended and onPollLimitReached is called
 * @param {number} state.backoff the next wait time in ms between polls
 * @param {number} state.polling means the 1st call responded with 'wait', so we are polling and recursively calling this
 * @returns {object} the json response from the endpoint
 */
async function pollMergedResume(options, pollOptions = {
  attemptLimit: ATTEMPT_LIMIT,
  baseBackoff: BASE_BACKOFF,
}, state = {
  attempt: 1,
  backoff: null,
  polling: false,
}) {
  const {
    onMergeFailure,
    onPollLimitReached,
    onMergeStartPoll = () => {},
    onMergeSuccess,
    onMergeNeedsFix,
    profileMethod = 'fe.zr-utilities.api.pollMergedResume',
  } = options;
  const {
    attemptLimit,
    baseBackoff,
  } = pollOptions;
  const {
    attempt,
    backoff,
    polling,
  } = state;

  const fetchInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      profile_method: profileMethod,
    }),
  };

  const response = await apiRequest(MERGE_RESUME_URL, fetchInit);
  const json = await response.json();
  const { merge_resume_status: mergeResumeStatus } = json;

  if (mergeResumeStatus === FAILURE) {
    onMergeFailure(json);
    return json;
  }

  if (mergeResumeStatus === SUCCESS) {
    onMergeSuccess(json);
    return json;
  }

  if (mergeResumeStatus === NEEDS_FIX) {
    onMergeNeedsFix(json);
    return json;
  }

  // if next request will reach attempt limit, give up
  if ((attempt + 1) > attemptLimit) {
    onPollLimitReached(json);
    return json;
  }

  if (mergeResumeStatus !== WAIT) {
    throw new Error(`Unrecognized merge resume status: ${mergeResumeStatus}`);
  }

  // if we got here, this means the status is 'wait'
  // so retry with exponential backoff

  if (!polling) {
    // this means we are starting to poll now, so let the caller know to show a loading spinner or something
    onMergeStartPoll(json);
  }

  const sleepTime = backoff || baseBackoff;
  await sleep(sleepTime);

  return pollMergedResume(options, pollOptions, {
    attempt: attempt + 1,
    backoff: sleepTime * 2,
    polling: true,
  });
}

/**
 * This calls the merge resume action relevant for parsed resume flows.
 *
 * It can run into the following results:
 * onMergeFailure: endpoint could not use parsed resume data, probably move on.
 * onPollLimitReached: parsing is still going on, but we waited too long and should move on.
 * onMergeSuccess: endpoint successfully merged parsed resume data into profile.
 * onMergeNeedsFix: endpoint returned parsed resume data that needs job seeker fixing.
 *
 * These callbacks include the json response, but tryMergedResume also returns it directly.
 *
 * If polling does happen, it means the parse task is ongoing. Exponential backoff is used.
 * In the case this does happen, onMergeStartPoll is called. Useful if we should show some UI while the user has to wait.
 *
 * @param {function} options.onMergeFailure called when merge status is 'failure'
 * @param {function} options.onPollLimitReached called when polling reached the max retries
 * @param {function} options.onMergeStartPoll called when polling has started (1st call says 'wait')
 * @param {function} options.onMergeSuccess called when merge status is 'success'
 * @param {function} options.onMergeNeedsFix called when merge status is 'needs_fix'
 * @param {string} options.profileMethod profile_method string for analytics
 * @returns {object} the json response from the endpoint
 */
export async function tryMergedResume(options) {
  // run polling with default polling options
  const result = await pollMergedResume(options);

  return result;
}

/**
 * Quicker version of tryMergedResume if polling does happen.
 * Quicker as in it will give up faster than the default polling.
 * See tryMergedResume for detailed jsdocs.
 *
 * @param {function} options.onMergeFailure called when merge status is 'failure'
 * @param {function} options.onPollLimitReached called when polling reached the max retries
 * @param {function} options.onMergeStartPoll called when polling has started (1st call says 'wait')
 * @param {function} options.onMergeSuccess called when merge status is 'success'
 * @param {function} options.onMergeNeedsFix called when merge status is 'needs_fix'
 * @param {function} options.profileMethod profile_method string for analytics
 * @returns {object} the json response from the endpoint
 */
export async function tryMergedResumeQuickPoll(options) {
  // run polling with fast polling options
  // this will try 4 times, waiting for a max of 3.5 seconds
  const result = await pollMergedResume(options, {
    attemptLimit: 4,
    baseBackoff: 500,
  });
  // for development in a dev environment, retry many times more since dev resume parsing is slow AF
  // DO NOT LEAVE THIS IN
  // const result = await pollMergedResume(options, {
  //   attemptLimit: 12,
  //   baseBackoff: 500,
  // });

  return result;
}

/**
 * Sends the job seeker's fixed data and merges that and parsed resume data into their profile.
 *
 * @param {object} fixedData the fixed data that the job seeker submitted
 * @returns {object} the json response from the endpoint
 */
export async function fixMergedResume(fixedData) {
  const fetchInit = {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
    },
    body: JSON.stringify({
      fixed_data: fixedData,
    }),
  };

  const response = await apiRequest(MERGE_RESUME_URL, fetchInit);
  const json = await response.json();

  return json;
}
